import type { Family } from "@/generated/core/v1/family";
import { addYears, compareDates, isSet, type PDate } from "@/plan-logic/lib/dates";
import { AGE_OPTIONS, members, type OnboardingDraft } from "@/plan-logic/questionnaire/draft";

/**
 * A retirement age that has already passed. The household's one retirement age applies to every
 * adult (the engine's `retirement_goal.retirement_age` on each person), and the engine can't plan a
 * retirement date before today, so an age someone has already reached is refused before saving:
 * on "Retiring comfortably" in onboarding and Priorities → Edit, and in Update Data, with the same
 * check and the same words.
 */

/** The age the engine plans around when none is given (`DEFAULT_RETIREMENT_AGE`, abi/assumptions.py). */
export const DEFAULT_RETIREMENT_AGE = 65;

/** An adult the retirement age applies to; `name` is blank for "you". */
export type RetiringAdult = { name: string; dob?: PDate };

/** True when someone born on `dob` reaches `age` on or before `today` (birthday today included). */
function reachedBy(dob: PDate | undefined, age: number, today: PDate): boolean {
  return isSet(dob) && compareDates(addYears(dob, age), today) <= 0;
}

/** The first adult (in order) who has reached `age` on or before `today`. */
function reached(adults: RetiringAdult[], age: number, today: PDate): RetiringAdult | undefined {
  return adults.find((a) => reachedBy(a.dob, age, today));
}

/**
 * True when a member of the stored household is retired: their retirement date (date of birth plus
 * their retirement age, or Abi's default when none is given) is on or before `today` (the user's
 * today, `User.currentDate` when set). The engine can't plan such a household yet (the retired
 * banner, DEVIATIONS.md).
 */
export function hasRetiredMember(family: Family, today: PDate): boolean {
  return family.persons.some((p) =>
    reachedBy(p.dob, p.retirementGoal?.retirementAge ?? DEFAULT_RETIREMENT_AGE, today),
  );
}

/** True when someone in the household has already reached the age Abi plans around by default. */
export function defaultRetirementAgePassed(adults: RetiringAdult[], today: PDate): boolean {
  return !!reached(adults, DEFAULT_RETIREMENT_AGE, today);
}

/**
 * Why `age` can't be the retirement age, or undefined when it's still ahead of everyone. Reaching
 * the age today counts as reached (the engine can't start a plan already retired by that age).
 * `isDefault`: the age is Abi's default, not one the user picked.
 */
export function retirementAgeIssue(
  adults: RetiringAdult[],
  age: number,
  today: PDate,
  isDefault = false,
): string | undefined {
  const who = reached(adults, age, today);
  if (!who) return undefined;
  const subject = who.name ? `${who.name} has` : "You've";
  const ahead = adults.length > 1 ? "you both" : "you";
  const reason = isDefault
    ? `${subject} already reached ${age}, the age Abi plans around unless you pick one.`
    : `${subject} already reached ${age}.`;
  return `${reason} Pick an age still ahead of ${ahead}.`;
}

/**
 * The ages offered: AGE_OPTIONS (50 to 75), carried on past the oldest adult's age when they're 75
 * or older, so there's always an age still ahead to pick (the engine allows up to 100).
 */
export function retirementAgeOptions(adults: RetiringAdult[], today: PDate): number[] {
  const last = AGE_OPTIONS[AGE_OPTIONS.length - 1];
  const oldest = Math.max(
    0,
    ...adults.filter((a) => isSet(a.dob)).map((a) => today.year - a.dob!.year),
  );
  const out = [...AGE_OPTIONS];
  for (let a = last + 1; a <= Math.min(100, oldest + 1); a++) out.push(a);
  return out;
}

// ------------------------------------------------------------------ Update Data

/** An adult as Update Data would save them: the birthday as edited, their own stored age. */
export type SavingAdult = RetiringAdult & { storedAge?: number };

/**
 * Update Data's retirement check, made on the values being saved together, so a mistake can always
 * be corrected (QA round 3): the birthdays as edited in the household section, and the age as
 * edited in the retirement card (`age`, undefined when none is picked).
 *
 * - `passed`: an age has passed for someone: Abi's default, the stored age, or anyone's own stored
 *   age (an adult added later has none, so the default applies to them). The age is then asked for
 *   and sent with a household save too, so the one age replaces every adult's.
 * - `askAge`: the age is shown (and sent with a retirement save): `passed`, or a target.
 * - `issue`: why the save is refused (the age shown has passed for someone), or undefined.
 */
export function householdRetirementCheck(
  adults: SavingAdult[],
  { target, age, storedAge }: { target: boolean; age?: number; storedAge?: number },
  today: PDate,
): { passed: boolean; askAge: boolean; age: number; issue?: string } {
  const passed =
    defaultRetirementAgePassed(adults, today) ||
    (storedAge !== undefined && !!retirementAgeIssue(adults, storedAge, today)) ||
    adults.some((a) => reachedBy(a.dob, a.storedAge ?? DEFAULT_RETIREMENT_AGE, today));
  const askAge = target || passed;
  const shown = age ?? DEFAULT_RETIREMENT_AGE;
  const issue = askAge
    ? retirementAgeIssue(adults, shown, today, !target && age === undefined)
    : undefined;
  return { passed, askAge, age: shown, issue };
}

// ------------------------------------------------------------------ onboarding

/** The onboarding draft's adults: you, then your partner (by first name) unless "Just me". */
export function draftAdults(draft: OnboardingDraft): RetiringAdult[] {
  return members(draft).map((p, i) => ({
    name: i === 0 ? "" : p.givenName.trim() || "Your partner",
    dob: p.dob,
  }));
}

/**
 * The age on "Retiring comfortably" (onboarding's priorities step), sent with every onboarding save
 * (v39: the target age is asked on every path): its problem, if someone has already reached it.
 */
export function draftRetirementAgeIssue(draft: OnboardingDraft, today: PDate): string | undefined {
  return retirementAgeIssue(draftAdults(draft), draft.retirement.age, today);
}
